import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { apiURL } from '../../../utilities/URLs'
import { account } from '../../../classes/Account'
import { refreshAccessToken } from '../../../utilities/extensions'
import SelectInvoiceStep from './components/SelectInvoiceStep'
import PaymentMethodStep from './components/PaymentMethodStep'
import ScheduleStep from './components/ScheduleStep'
import ReviewStep from './components/ReviewStep'
import { previewSchedule, tomorrowIsoDate, clampFirstPaymentDate, isFirstPaymentDateAllowed } from './installmentFormat'
import { isCardPaymentReady } from '../../../components/CreditCardInput/creditCard'

export default function PaymentInstallmentsWizardPage () {
  const { applicationId } = useParams()
  const [searchParams] = useSearchParams()
  const quarterParam = searchParams.get('quarter') || ''
  const navigate = useNavigate()

  const [invoices, setInvoices] = useState([])
  const [savedAch, setSavedAch] = useState(null)
  const [invoice, setInvoice] = useState(null)
  const [method, setMethod] = useState({ mode: 'card', methodType: 'card' })
  const [frequency, setFrequency] = useState('monthly')
  const [installmentCount, setInstallmentCount] = useState(3)
  const [startDate, setStartDate] = useState(() => tomorrowIsoDate())
  const [step, setStep] = useState(quarterParam ? 1 : 0)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const load = async () => {
      const res = await fetch(`${apiURL}/api/applications/${applicationId}/payment-installments${quarterParam ? `?quarter=${encodeURIComponent(quarterParam)}` : ''}`, {
        headers: { Authorization: account.accessToken }
      })
      const data = await res.json()
      if (data.response?.error === 'Failed To Authenticate Token.') {
        const refreshed = await refreshAccessToken()
        if (refreshed) return load()
        return navigate('/', { state: { expired: true } })
      }
      if (data.response?.error) {
        setError(data.response.error)
        return
      }
      setInvoices(data.response.invoices || [])
      setSavedAch(data.response.savedAch || null)
      if (quarterParam && (data.response.invoices || []).length === 1)
        setInvoice(data.response.invoices[0])
      if (quarterParam && (data.response.invoices || []).length > 1)
        setStep(0)
    }
    load()
  }, [applicationId, quarterParam, navigate])

  const schedule = useMemo(() => {
    if (!invoice || !startDate) return []
    return previewSchedule(startDate, frequency, installmentCount, invoice.invoiceAmount)
  }, [invoice, startDate, frequency, installmentCount])

  const resolvePaymentMethod = async () => {
    if (method.mode === 'saved' && method.customerVaultId) return method

    if (method.methodType === 'card' || method.mode === 'card') {
      const res = await fetch(`${apiURL}/api/applications/${applicationId}/payment-installments/card-vault`, {
        method: 'POST',
        headers: { Authorization: account.accessToken, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ccnumber: method.ccnumber,
          ccexp: method.ccexp,
          cvv: method.cvv
        })
      })
      const data = await res.json()
      if (data.response?.error) throw new Error(data.response.error)
      return { ...method, methodType: 'card', ...data.response }
    }

    const res = await fetch(`${apiURL}/api/applications/${applicationId}/bank-verification`, {
      method: 'POST',
      headers: { Authorization: account.accessToken, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        bankName: method.bankName,
        accountName: method.accountName,
        accountType: method.accountType || 'checking',
        accountHolderType: method.accountHolderType || 'business',
        accountNumber: method.accountNumber,
        routingNumber: method.routingNumber,
        firstName: method.firstName,
        lastName: method.lastName,
        address1: method.address1,
        city: method.city,
        state: method.state,
        zip: method.zip
      })
    })
    const data = await res.json()
    if (data.response?.error) throw new Error(data.response.error)
    const vault = data.response.bankVerification || data.response
    return {
      methodType: 'ach',
      customerVaultId: vault.customerVaultId,
      last4: String(vault.maskedAccountNumber || method.accountNumber || '').slice(-4),
      methodLabel: method.bankName || 'Bank'
    }
  }

  const handleSave = async () => {
    setSaving(true)
    setError('')
    try {
      const resolved = await resolvePaymentMethod()
      const res = await fetch(`${apiURL}/api/applications/${applicationId}/payment-installments`, {
        method: 'POST',
        headers: { Authorization: account.accessToken, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          invoiceId: invoice.invoiceId,
          frequency,
          installmentCount,
          startDate,
          methodType: resolved.methodType,
          customerVaultId: resolved.customerVaultId,
          last4: resolved.last4,
          methodLabel: resolved.methodLabel
        })
      })
      const data = await res.json()
      if (data.response?.error) throw new Error(data.response.error)
      navigate(`/applications/${applicationId}/payment-installments/${data.response.planId}`)
    } catch (err) {
      setError(err.message || 'Could not save payment plan.')
    } finally {
      setSaving(false)
    }
  }

  const canContinue = () => {
    if (step === 0) return Boolean(invoice)
    if (step === 1) {
      if (method.mode === 'saved' && method.customerVaultId) return true
      if (method.mode === 'card' || method.methodType === 'card')
        return isCardPaymentReady(method)
      return method.routingNumber && method.accountNumber && method.bankName && method.firstName && method.address1
    }
    if (step === 2) return isFirstPaymentDateAllowed(startDate) && installmentCount >= 2
    return true
  }

  return (
    <div className='w-full max-w-5xl p-4 sm:p-8'>
      <button type='button' onClick={() => navigate(-1)} className='flex items-center gap-2 text-sm font-semibold text-slate-500 mb-6'>
        <ArrowLeft size={16} /> Back
      </button>
      <h1 className='text-3xl font-bold text-slate-900 dark:text-white mb-2'>Payment installments</h1>
      <p className='text-slate-600 dark:text-slate-300 mb-8'>Set up a weekly or monthly plan for one unpaid invoice.</p>

      {error && <p className='mb-4 text-sm text-rose-600'>{error}</p>}

      {step === 0 && <SelectInvoiceStep invoices={invoices} selectedId={invoice?.invoiceId} onSelect={setInvoice} />}
      {step === 1 && <PaymentMethodStep savedAch={savedAch} method={method} onChange={setMethod} />}
      {step === 2 && (
        <ScheduleStep
          invoice={invoice}
          frequency={frequency}
          installmentCount={installmentCount}
          startDate={startDate}
          schedule={schedule}
          onChange={(next) => {
            if (next.frequency) setFrequency(next.frequency)
            if (next.installmentCount) setInstallmentCount(next.installmentCount)
            if (next.startDate) setStartDate(clampFirstPaymentDate(next.startDate))
          }}
        />
      )}
      {step === 3 && (
        <ReviewStep
          invoice={invoice}
          method={method}
          frequency={frequency}
          installmentCount={installmentCount}
          startDate={startDate}
          schedule={schedule}
        />
      )}

      <div className='flex justify-between mt-8'>
        <button
          type='button'
          disabled={step === (quarterParam ? 1 : 0)}
          onClick={() => setStep((s) => s - 1)}
          className='px-4 py-2 rounded-xl text-sm font-semibold border border-gray-200 dark:border-slate-600 disabled:opacity-40'
        >
          Back
        </button>
        {step < 3 ? (
          <button
            type='button'
            disabled={!canContinue()}
            onClick={() => setStep((s) => s + 1)}
            className='px-5 py-2 rounded-xl text-sm font-semibold bg-sky-600 text-white disabled:opacity-40'
          >
            Continue
          </button>
        ) : (
          <button
            type='button'
            disabled={saving}
            onClick={handleSave}
            className='px-5 py-2 rounded-xl text-sm font-semibold bg-sky-600 text-white disabled:opacity-40'
          >
            {saving ? 'Saving…' : 'Save payment plan'}
          </button>
        )}
      </div>
    </div>
  )
}
